import React from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { TargetROASInput } from './target-roas-input';
import { TargetCPAInput } from './target-cpa-input';
import { usePostHog } from 'posthog-js/react';

export type BiddingStrategyType = 'MAXIMIZE_CONVERSIONS' | 'MAXIMIZE_CONVERSION_VALUE' | 'TARGET_SPEND';

interface BiddingStrategySelectorProps {
  biddingStrategyType: BiddingStrategyType;
  setBiddingStrategyType: (strategy: BiddingStrategyType) => void;
  targetRoas?: number;
  setTargetRoas?: (value: number) => void;
  targetCpa?: number;
  setTargetCpa?: (value: number) => void;
  conversionTrackingEnabled?: boolean;
  disabled?: boolean;
}

export const BiddingStrategySelector: React.FC<BiddingStrategySelectorProps> = ({
  biddingStrategyType,
  setBiddingStrategyType,
  targetRoas,
  setTargetRoas,
  targetCpa,
  setTargetCpa,
  conversionTrackingEnabled = true,
  disabled = false,
}) => {
  const posthog = usePostHog();
  return (
    <div>
      <h2 className='text-xl font-semibold text-gray-900'>Bidding Strategy</h2>
      <p className='text-sm text-gray-600 mt-1 mb-4'>
        Choose how to optimize your bidding.
      </p>
      <RadioGroup
          value={biddingStrategyType}
          onValueChange={(value) => {
            setBiddingStrategyType(value as BiddingStrategyType);
            posthog?.capture('bidding_strategy_changed', { strategy: value });
          }}
          disabled={disabled}
          className='space-y-4'
        >
          <div className='flex items-start space-x-3'>
            <RadioGroupItem value='TARGET_SPEND' id='target-spend' className='mt-1' />
            <div className='flex-1'>
              <Label htmlFor='target-spend' className='cursor-pointer font-normal'>
                <div className='font-semibold text-gray-900'>Maximize Clicks</div>
                <div className='text-sm text-gray-500 mt-1'>
                  Get as many clicks as possible within your budget.
                </div>
              </Label>
            </div>
          </div>
          <div className='flex items-start space-x-3'>
            <RadioGroupItem value='MAXIMIZE_CONVERSIONS' id='maximize-conversions' className='mt-1' />
            <div className='flex-1'>
              <Label htmlFor='maximize-conversions' className='cursor-pointer font-normal'>
                <div className='font-semibold text-gray-900'>Maximize Conversions</div>
                <div className='text-sm text-gray-500 mt-1'>
                  Get the most conversions from your budget.
                </div>
              </Label>
              {biddingStrategyType === 'MAXIMIZE_CONVERSIONS' && setTargetCpa && (
                <div className='mt-3'>
                  <TargetCPAInput
                    targetCpa={targetCpa}
                    setTargetCpa={setTargetCpa}
                    disabled={disabled}
                    conversionTrackingEnabled={conversionTrackingEnabled}
                  />
                </div>
              )}
            </div>
          </div>
          <div className='flex items-start space-x-3'>
            <RadioGroupItem value='MAXIMIZE_CONVERSION_VALUE' id='maximize-conversion-value' className='mt-1' />
            <div className='flex-1'>
              <Label htmlFor='maximize-conversion-value' className='cursor-pointer font-normal'>
                <div className='font-semibold text-gray-900'>Target ROAS</div>
                <div className='text-sm text-gray-500 mt-1'>
                  Achieve a specific return on ad spend (ROAS).
                </div>
              </Label>
              {biddingStrategyType === 'MAXIMIZE_CONVERSION_VALUE' && setTargetRoas && (
                <div className='mt-3'>
                  <TargetROASInput
                    targetRoas={targetRoas}
                    setTargetROAS={setTargetRoas}
                    disabled={disabled}
                    showPercentage={true}
                  />
                </div>
              )}
            </div>
          </div>
      </RadioGroup>
    </div>
  );
};
